import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import NearbyGarages from "@/components/NearbyGarages";
import { Sparkles, AlertTriangle, Wrench, Clock, Loader2, RefreshCw, CheckCircle2, XCircle, MapPin } from "lucide-react";

const SEVERITY_STYLES = {
  "Minor":      { bg: "#E6F9F0", fg: "#00A86B", icon: CheckCircle2 },
  "Moderate":   { bg: "#FFF6E0", fg: "#A05A00", icon: AlertTriangle },
  "Severe":     { bg: "#FFEDED", fg: "#C8102E", icon: AlertTriangle },
  "Total Loss": { bg: "#1A1A1A", fg: "#FFFFFF", icon: XCircle },
};

export default function DamageEstimator({ docId, autoLoad = false, pincode, insurer, claimId, customerMobile, customerName }) {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasExisting, setHasExisting] = useState(false);
  const [showGarages, setShowGarages] = useState(false);

  // Check if an estimate already exists
  useEffect(() => {
    let cancelled = false;
    api.get(`/ai/vision/damage-estimate/${docId}`)
      .then((r) => { if (!cancelled) { setEstimate(r.data); setHasExisting(true); } })
      .catch(() => { /* none yet */ });
    return () => { cancelled = true; };
  }, [docId]);

  const run = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/ai/vision/damage-estimate", { doc_id: docId });
      setEstimate(data);
      setHasExisting(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not analyse image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad && !hasExisting && !loading && !estimate) {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  if (!estimate && !loading && !error) {
    return (
      <div className="mt-3 p-3 rounded-xl border border-dashed border-[#003DA5]/30 bg-[#E6F0FF]/30 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`damage-cta-${docId}`}>
        <div className="flex items-center gap-2 flex-1">
          <Sparkles className="w-4 h-4 text-[#003DA5]" />
          <span className="text-xs sm:text-sm font-semibold text-slate-800">
            Get an instant AI damage estimate — Gemini Vision analyses your photo and gives severity, affected parts and repair days.
          </span>
        </div>
        <button
          onClick={run}
          className="bg-[#003DA5] hover:bg-[#002F80] text-white rounded-lg px-4 py-2 text-xs font-bold uppercase tracking-wider whitespace-nowrap"
          data-testid={`damage-run-${docId}`}
        >
          Analyse with AI
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mt-3 p-4 rounded-xl border border-[#003DA5]/30 bg-[#E6F0FF]/40 flex items-center gap-3 fade-in-up" data-testid={`damage-loading-${docId}`}>
        <Loader2 className="w-5 h-5 text-[#003DA5] animate-spin shrink-0" />
        <div>
          <div className="text-sm font-bold text-slate-900">AI is inspecting your photo…</div>
          <div className="text-xs text-slate-600">Identifying damaged components and estimating repair scope.</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3 p-3 rounded-xl border border-red-200 bg-red-50 flex items-center justify-between gap-3" data-testid={`damage-error-${docId}`}>
        <div className="flex items-center gap-2 text-sm text-red-700">
          <XCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
        <button onClick={run} className="text-xs font-bold text-red-700 hover:underline">Retry</button>
      </div>
    );
  }

  if (!estimate) return null;

  if (estimate.is_vehicle === false) {
    return (
      <div className="mt-3 p-3 rounded-xl border border-amber-200 bg-amber-50" data-testid={`damage-not-vehicle-${docId}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
          <AlertTriangle className="w-4 h-4" /> AI couldn't detect a vehicle in this photo
        </div>
        <p className="text-xs text-amber-700 mt-1">{estimate.recommendation || "Please upload a clear photo of the damaged area."}</p>
      </div>
    );
  }

  const sev = SEVERITY_STYLES[estimate.severity] || SEVERITY_STYLES["Minor"];
  const SevIcon = sev.icon;
  const confPct = Math.round((estimate.confidence ?? 0) * 100);

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden fade-in-up" data-testid={`damage-result-${docId}`}>
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#003DA5]" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">AI Damage Estimate</span>
        </div>
        <button
          onClick={run}
          disabled={loading}
          className="text-xs font-semibold text-slate-500 hover:text-[#003DA5] flex items-center gap-1"
          data-testid={`damage-rerun-${docId}`}
        >
          <RefreshCw className="w-3 h-3" /> Re-analyse
        </button>
      </div>

      <div className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="px-3 py-1.5 rounded-full flex items-center gap-1.5" style={{ background: sev.bg, color: sev.fg }}>
            <SevIcon className="w-3.5 h-3.5" />
            <span className="text-xs font-bold uppercase tracking-wider" data-testid="damage-severity">{estimate.severity}</span>
          </div>
          <div className="text-xs text-slate-500">Confidence <span className="font-bold text-slate-700">{confPct}%</span></div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <Pill icon={Wrench} label="Repair Type" value={estimate.repair_complexity} color="#003DA5" testid="damage-complexity" />
          <Pill icon={Clock} label="Repair Time" value={`${estimate.estimated_days_min}-${estimate.estimated_days_max} days`} color="#FF7A00" testid="damage-days" />
          <Pill icon={Sparkles} label="Parts Damaged" value={`${estimate.damaged_parts?.length || 0} identified`} color="#00A86B" testid="damage-parts-count" />
        </div>

        {(estimate.estimated_cost_inr_min > 0 || estimate.estimated_cost_inr_max > 0) && (
          <div className="mb-4 p-4 rounded-xl bg-gradient-to-br from-[#003DA5] to-[#0A5BBD] text-white" data-testid="damage-cost-band">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80 mb-1">Indicative repair cost</div>
            <div className="font-heading font-extrabold text-2xl tracking-tight">
              ₹{(estimate.estimated_cost_inr_min || 0).toLocaleString("en-IN")} <span className="opacity-60 text-lg">–</span> ₹{(estimate.estimated_cost_inr_max || 0).toLocaleString("en-IN")}
            </div>
            <div className="text-[11px] opacity-80 mt-0.5">
              Mid-segment vehicle, network garage, parts + labour. Final amount confirmed by surveyor.
            </div>
          </div>
        )}

        {estimate.damaged_parts?.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Visible damage</div>
            <div className="flex flex-wrap gap-1.5">
              {estimate.damaged_parts.map((p, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full bg-slate-100 text-xs font-semibold text-slate-700" data-testid={`damage-part-${i}`}>
                  {p}
                </span>
              ))}
            </div>
          </div>
        )}

        {estimate.recommendation && (
          <div className="mt-3 p-3 rounded-lg bg-[#F8FAFC] border border-slate-100 text-sm text-slate-700 leading-relaxed" data-testid="damage-recommendation">
            <span className="font-bold text-slate-900">Recommendation: </span>{estimate.recommendation}
          </div>
        )}

        {(pincode || insurer) && estimate.is_vehicle !== false && (
          <button
            onClick={() => setShowGarages(true)}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF7A00] to-[#FF9633] hover:from-[#CC6200] hover:to-[#E07A00] text-white rounded-xl px-4 py-3 text-sm font-bold transition-colors"
            data-testid={`garage-handoff-${docId}`}
          >
            <MapPin className="w-4 h-4" /> Find nearby cashless garages
          </button>
        )}

        {showGarages && (
          <NearbyGarages
            pincode={pincode}
            insurer={insurer}
            claimId={claimId}
            docIds={[docId]}
            customerMobile={customerMobile}
            customerName={customerName}
            speciality={estimate.repair_complexity === "Structural" ? "structural" : estimate.repair_complexity === "Panel replacement" ? "panel" : "cosmetic"}
            onClose={() => setShowGarages(false)}
          />
        )}

        <div className="mt-3 flex items-center justify-between text-[10px] text-slate-400">
          <span>Powered by {estimate.model || "Gemini Vision"}</span>
          <span>{estimate.at ? new Date(estimate.at).toLocaleString() : ""}</span>
        </div>

        <div className="mt-2 text-[10px] text-slate-400 leading-relaxed">
          AI estimate is indicative only. Final assessment is by the licensed surveyor appointed by your insurer.
        </div>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="p-2.5 rounded-lg border border-slate-100 bg-slate-50" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <div className="text-sm font-bold text-slate-900">{value}</div>
    </div>
  );
}
